import { z } from 'zod';
import { LLM_PROVIDERS } from './llm-providers';

/**
 * VoiceStackConfig — the voice / speed / language selection for a
 * single call, decided by the API-side resolver and carried through to
 * the livekit agent via session inputs.
 *
 * ## Why it's separate from `VoiceSettings`
 *
 * `VoiceSettings` is a legacy per-agent-config TTS-only shape used by
 * the outbound handler (from `AgentConfig.voice`). `VoiceStackConfig`
 * is the unified shape for the multi-agent runtime path: STT + TTS +
 * language together, with the resolver's decision metadata included
 * so the agent (and logs) can see WHY this stack was chosen.
 *
 * ## Decision pipeline (R-1)
 *
 *   patient preference → org default → call-reason default → global fallback
 *
 * The API-side resolver walks this chain at call setup and stamps the
 * winning `source`. The livekit agent consumes the final shape without
 * needing to know the chain.
 */

export const voiceStackProviderSchema = z.object({
  provider: z.enum(['elevenlabs', 'cartesia', 'deepgram', 'openai']),
  voiceId: z.string(),
  model: z.string().optional(),
});

export const transcriberStackProviderSchema = z.object({
  provider: z.enum(['deepgram', 'openai']),
  model: z.string().optional(),
});

export const llmStackProviderSchema = z.object({
  provider: LLM_PROVIDERS,
  model: z.string(),
});

export const voiceStackSourceSchema = z.enum([
  'patient',
  'organization',
  'call-reason',
  'global-default',
]);
export type VoiceStackSource = z.infer<typeof voiceStackSourceSchema>;

export const voiceStackConfigSchema = z.object({
  /**
   * IETF BCP 47 language tag. Drives STT language and the prompt's
   * persona guidance. Defaults to 'en-US'.
   *
   * NOTE: The field is threaded through `AgentSessionInputs` and
   * rendered into Mustache prompt context as `language`. When
   * multi-language prompts land, per-locale `.md` files branch on
   * this. Today all prompts are English; the field is plumbing only.
   */
  language: z.string().default('en-US'),

  /** TTS speech rate multiplier. 1.0 = natural. */
  speed: z.number().min(0.5).max(2.0).default(1.0),

  tts: voiceStackProviderSchema,
  stt: transcriberStackProviderSchema,

  /**
   * LLM providers in failover order. Index 0 is the primary; indices
   * 1..N are fallbacks tried in order when the primary errors or times
   * out. The livekit agent wraps them in `llm.FallbackAdapter` so
   * failover is transparent to the agent loop.
   *
   * At least one provider must be present; one provider + no fallbacks
   * is a valid (degraded-reliability) configuration.
   */
  llm: z.array(llmStackProviderSchema).min(1),

  /**
   * Which layer of the resolver chain produced this stack. Stamped by
   * the API; logged by the livekit agent for debug/observability.
   */
  source: voiceStackSourceSchema,
});

export type VoiceStackConfig = z.infer<typeof voiceStackConfigSchema>;
export type VoiceStackProvider = z.infer<typeof voiceStackProviderSchema>;
export type TranscriberStackProvider = z.infer<typeof transcriberStackProviderSchema>;
export type LlmStackProvider = z.infer<typeof llmStackProviderSchema>;

/**
 * Global fallback used when no patient/org/call-reason preference
 * exists. Mirrors the current hardcoded livekit defaults so existing
 * calls stay on the same stack during rollout. Frozen to prevent
 * accidental mutation — callers must clone via spread if they need to
 * tweak a field.
 */
export const GLOBAL_DEFAULT_VOICE_STACK: VoiceStackConfig = Object.freeze({
  language: 'en-US',
  speed: 1.0,
  tts: Object.freeze({
    provider: 'cartesia',
    voiceId: '79a125e8-cd45-4c13-8a67-188112f4dd22',
    model: 'sonic-2',
  }),
  stt: Object.freeze({
    provider: 'deepgram',
    model: 'nova-3',
  }),
  // Primary: OpenAI gpt-5 — Gemini 3 Flash Preview was the prior primary
  // but real-call traffic surfaced two failure modes that hurt patient
  // experience: (1) the `MALFORMED_FUNCTION_CALL` historical issue
  // recurring on multi-tool turns, and (2) a `Function call is missing
  // a thought_signature` Gemini 3 bug fired on parallel tool calls (e.g.
  // `save_contact_preference` + `send_confirmation_text`) that forced
  // a fallback hop and added ~1.5–2s latency. gpt-5 handles parallel
  // tool calls and complex prompt instructions more reliably; voice
  // first-token latency is in-class with Flash on observed turns.
  // Fallback: Gemini 2.5 Flash — kept for cross-provider resilience
  // (different infrastructure, so a regional OpenAI outage doesn't
  // black out the call path).
  // ⚠ Compliance: confirm the BAA is in place with OpenAI before this
  // ships to production patient calls. Prior comment flagged the
  // absence; this default assumes the situation has changed. If not,
  // demote OpenAI back to a non-prod-only stack and restore Gemini 2.5
  // as the production primary.
  llm: Object.freeze([
    Object.freeze({ provider: 'openai', model: 'gpt-5' }),
    Object.freeze({ provider: 'google', model: 'gemini-2.5-flash' }),
  ]) as never,
  source: 'global-default',
}) as VoiceStackConfig;
